'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import HowItWorksCarousel from './HowItWorksCarousel';

const DEMO_VIDEO_SRC =
  'https://hsfugwgkkzjbvjqomqkc.supabase.co/storage/v1/object/public/videos/how-it-works-demo.mp4';

const DEMO_SUBTITLE_CUES = [
  { start: 0, end: 5.5, text: 'If you run a school or tutoring centre, you already know this problem.' },
  { start: 5.5, end: 10.5, text: 'Checking takes hours, but students still have hidden gaps.' },
  { start: 10.5, end: 15.8, text: 'Do you teach without knowing what students truly understand?' },
  { start: 15.8, end: 20.4, text: 'Are you stuck between creating worksheets, grading, and parent updates?' },
  { start: 20.4, end: 24.6, text: 'IntelGrader is the academic intelligence layer.' },
  { start: 24.6, end: 29.8, text: 'It turns everyday homework and tests into actionable learning insights.' },
  { start: 29.8, end: 37.8, text: 'Link any ebook or syllabus. Create assignments in minutes. Auto-grade handwritten work with state-of-the-art OCR.' },
  { start: 37.8, end: 46.5, text: "Share each student's learning health report with parents. Track concept-level and formula-level mastery, gaps, and what to teach next." },
  { start: 46.5, end: 50.3, text: 'Less checking. Clear learning health. Better outcomes.' },
  { start: 50.3, end: 52.5, text: 'Want a quick demo? Visit our website.' },
] as const;

function getSubtitleAtTime(currentTime: number) {
  return DEMO_SUBTITLE_CUES.find(({ start, end }) => currentTime >= start && currentTime < end)?.text ?? '';
}

const HowItWorks: React.FC = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [storyActive, setStoryActive] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isIntroVideoReady, setIsIntroVideoReady] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [previewSubtitle, setPreviewSubtitle] = useState('');
  const [modalSubtitle, setModalSubtitle] = useState('');
  const [shouldLoadPreview, setShouldLoadPreview] = useState(false);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const modalVideoRef = useRef<HTMLVideoElement>(null);
  const storyRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const videoCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const card = videoCardRef.current;
    if (!card || shouldLoadPreview) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadPreview(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px 0px' }
    );
    observer.observe(card);
    return () => observer.disconnect();
  }, [shouldLoadPreview]);

  const syncTracks = (video: HTMLVideoElement) => {
    for (const track of Array.from(video.textTracks)) {
      track.mode = track.language === 'en' ? 'hidden' : 'disabled';
    }
  };

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReducedMotion(mq.matches);
    update();

    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) {
      setIsIntroVideoReady(true);
      setPreviewSubtitle('');
      return;
    }

    if (!shouldLoadPreview) return;

    const video = previewVideoRef.current;
    if (!video) return;

    const syncMutedState = () => setIsMuted(video.muted || video.volume === 0);
    const syncSubtitle = () => setPreviewSubtitle(getSubtitleAtTime(video.currentTime));
    const markReady = () => {
      setIsIntroVideoReady(true);
      syncTracks(video);
      syncSubtitle();
    };

    video.muted = isMuted;
    syncTracks(video);
    syncSubtitle();

    if (video.readyState >= 2) markReady();

    video.addEventListener('loadeddata', markReady);
    video.addEventListener('canplay', markReady);
    video.addEventListener('playing', markReady);
    video.addEventListener('loadedmetadata', markReady);
    video.addEventListener('timeupdate', syncSubtitle);
    video.addEventListener('seeked', syncSubtitle);
    video.addEventListener('ended', syncSubtitle);
    video.addEventListener('volumechange', syncMutedState);

    void video.play().catch(() => undefined);

    return () => {
      video.removeEventListener('loadeddata', markReady);
      video.removeEventListener('canplay', markReady);
      video.removeEventListener('playing', markReady);
      video.removeEventListener('loadedmetadata', markReady);
      video.removeEventListener('timeupdate', syncSubtitle);
      video.removeEventListener('seeked', syncSubtitle);
      video.removeEventListener('ended', syncSubtitle);
      video.removeEventListener('volumechange', syncMutedState);
    };
  }, [prefersReducedMotion, shouldLoadPreview]);

  useEffect(() => {
    const el = storyRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setStoryActive(entry.isIntersecting && entry.intersectionRatio > 0.35);
      },
      {
        threshold: [0.2, 0.35, 0.6],
        rootMargin: '-10% 0px -18% 0px',
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const videos = [previewVideoRef.current, modalVideoRef.current];

    for (const video of videos) {
      if (!video) continue;
      if (video.muted !== isMuted) video.muted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    const previewVideo = previewVideoRef.current;

    if (isVideoOpen) {
      previewVideo?.pause();
      return;
    }

    if (!prefersReducedMotion) {
      void previewVideo?.play().catch(() => undefined);
    }
  }, [isVideoOpen, prefersReducedMotion]);

  useEffect(() => {
    const video = modalVideoRef.current;
    if (!isVideoOpen || !video) return;

    const syncMutedState = () => setIsMuted(video.muted || video.volume === 0);
    const syncSubtitle = () => setModalSubtitle(getSubtitleAtTime(video.currentTime));
    const prepareVideo = () => {
      syncTracks(video);
      syncSubtitle();
      (video.parentElement as HTMLElement | null)?.classList.add('is-loaded');
    };

    video.currentTime = 0;
    video.muted = isMuted;
    syncTracks(video);
    syncSubtitle();

    if (video.readyState >= 2) prepareVideo();

    video.addEventListener('loadeddata', prepareVideo);
    video.addEventListener('canplay', prepareVideo);
    video.addEventListener('loadedmetadata', prepareVideo);
    video.addEventListener('playing', syncSubtitle);
    video.addEventListener('timeupdate', syncSubtitle);
    video.addEventListener('seeked', syncSubtitle);
    video.addEventListener('ended', syncSubtitle);
    video.addEventListener('volumechange', syncMutedState);

    void video.play().catch(() => undefined);

    return () => {
      video.pause();
      video.currentTime = 0;
      video.removeEventListener('loadeddata', prepareVideo);
      video.removeEventListener('canplay', prepareVideo);
      video.removeEventListener('loadedmetadata', prepareVideo);
      video.removeEventListener('playing', syncSubtitle);
      video.removeEventListener('timeupdate', syncSubtitle);
      video.removeEventListener('seeked', syncSubtitle);
      video.removeEventListener('ended', syncSubtitle);
      video.removeEventListener('volumechange', syncMutedState);
      setModalSubtitle('');
    };
  }, [isVideoOpen]);

  // Scroll-driven video tilt + fade
  useEffect(() => {
    if (prefersReducedMotion) return;

    const header = headerRef.current;
    const card = videoCardRef.current;
    if (!header || !card) return;

    let ticking = false;

    const stickyRegion = header.closest('.how-sticky-region') as HTMLElement;
    const section = stickyRegion.closest('section') as HTMLElement;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        // Compute fresh each frame — section height can change after mount
        const stickyRegionDocTop = section.offsetTop + stickyRegion.offsetTop;
        const headerH = header.offsetHeight;
        const stickyRegionH = stickyRegion.offsetHeight;
        const fadeStartScroll = stickyRegionDocTop + headerH * 0.5;
        const fadeDuration = Math.min(stickyRegionH - headerH, 250);
        const progress = Math.max(0, Math.min(1, (window.scrollY - fadeStartScroll) / fadeDuration));

        const rotateX = progress * 28;
        const translateZ = progress * -400;
        const scale = 1 - progress * 0.25;
        const opacity = 1 - progress;

        card.style.transform = `rotateX(${rotateX}deg) translateZ(${translateZ}px) scale(${scale})`;
        card.style.opacity = String(opacity);

        // Also fade the label below the video
        const label = card.parentElement?.querySelector('.how-video-card__body') as HTMLElement | null;
        if (label) label.style.opacity = String(opacity);

        // Hide the video area visually once fully faded (keep layout for sticky math)
        const demoTop = card.closest('.how-demo-top') as HTMLElement | null;
        if (demoTop) {
          demoTop.style.visibility = progress >= 0.95 ? 'hidden' : '';
        }

        // Reveal the steps carousel only after video fully fades
        const storyWrap = document.querySelector('.how-stage__story-wrap') as HTMLElement | null;
        if (storyWrap) {
          if (progress >= 0.92) {
            storyWrap.classList.add('is-revealed');
          } else {
            storyWrap.classList.remove('is-revealed');
          }
        }
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener('scroll', onScroll);
  }, [prefersReducedMotion]);

  const toggleMuted = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    setIsMuted((current) => !current);
  };

  return (
    <section id="how-it-works" className={`how-stage relative py-16 px-6 scroll-mt-32 ${storyActive ? 'how-stage--story' : ''}`}>
      {/* Sticky region: header + video only */}
      <div className="how-sticky-region">
        <div ref={headerRef} className={`section-header section-header--secondary section-header--how how-stage__header ${storyActive ? 'is-muted' : ''}`}>
          <span className="section-eyebrow">How IntelGrader Works</span>
          <h2 className="section-title">From classwork to concept clarity.</h2>
          <p className="section-subtitle">
            Map your curriculum, grade with precision, surface concept gaps — automatically.
          </p>
        </div>

        {/* Demo video */}
        <div className="how-stage__intro-wrap">
          <div className={`how-demo-top how-stage__intro ${storyActive ? 'is-muted' : ''}`}>
            <div ref={videoCardRef} className="how-video-card">
              <div className="how-video-card__preview">
                <div className={`video-skeleton how-video-card__skeleton ${isIntroVideoReady ? 'is-loaded' : ''}`}>
                  {prefersReducedMotion || !shouldLoadPreview ? (
                    <Image
                      src="/how-it-works-thumb.png"
                      alt="IntelGrader product walkthrough preview"
                      className="how-video-card__video"
                      width={960}
                      height={540}
                      sizes="(max-width: 768px) 100vw, 720px"
                      loading="lazy"
                      decoding="async"
                      onLoad={() => setIsIntroVideoReady(true)}
                    />
                  ) : (
                    <video
                      ref={previewVideoRef}
                      className="how-video-card__video"
                      poster="/how-it-works-thumb.png"
                      src={DEMO_VIDEO_SRC}
                      autoPlay
                      muted={isMuted}
                      loop
                      playsInline
                      preload="auto"
                      onLoadedData={() => setIsIntroVideoReady(true)}
                      onCanPlay={() => setIsIntroVideoReady(true)}
                      onPlaying={() => setIsIntroVideoReady(true)}
                    >
                      <track
                        default
                        kind="captions"
                        src="/how-it-works-demo.vtt"
                        srcLang="en"
                        label="English"
                      />
                    </video>
                  )}
                </div>
                {previewSubtitle && !prefersReducedMotion && (
                  <div className="how-demo-video__subtitle-wrap">
                    <div className="how-demo-video__subtitle">{previewSubtitle}</div>
                  </div>
                )}
                {!prefersReducedMotion && (
                  <button
                    type="button"
                    className="how-demo-video__mute"
                    onClick={toggleMuted}
                    aria-label={isMuted ? 'Video is muted. Click to unmute.' : 'Video sound is on. Click to mute.'}
                  >
                    {isMuted ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                        <line x1="23" y1="9" x2="17" y2="15" />
                        <line x1="17" y1="9" x2="23" y2="15" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                      </svg>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  className="how-video-card__preview-hitarea"
                  onClick={() => setIsVideoOpen(true)}
                  aria-label="Open product walkthrough video"
                />
              </div>
              <div className="how-video-card__body">
                <h3 className="how-video-card__title">Watch the 2-min walkthrough</h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step carousel — outside sticky region */}
      <div
        ref={storyRef}
        className={`how-stage__story how-stage__story-wrap ${storyActive ? 'is-active' : ''}`}
      >
        <HowItWorksCarousel />
      </div>

      {/* Video Modal */}
      {isVideoOpen && (
        <div className="how-video-modal" role="dialog" aria-modal="true">
          <button
            type="button"
            className="how-video-modal__backdrop"
            onClick={() => setIsVideoOpen(false)}
            aria-label="Close video"
          />
          <div className="how-video-modal__content">
            <button
              type="button"
              className="how-video-modal__close"
              onClick={() => setIsVideoOpen(false)}
              aria-label="Close video"
            >
              &#10005;
            </button>
            <div className="video-skeleton">
              <video
                ref={modalVideoRef}
                controls
                autoPlay
                className="how-video-modal__player"
                src={DEMO_VIDEO_SRC}
                width="1280"
                height="720"
                muted={isMuted}
                playsInline
                preload="metadata"
              >
                <track
                  default
                  kind="captions"
                  src="/how-it-works-demo.vtt"
                  srcLang="en"
                  label="English"
                />
              </video>
              {modalSubtitle && (
                <div className="how-demo-video__subtitle-wrap how-demo-video__subtitle-wrap--modal">
                  <div className="how-demo-video__subtitle how-demo-video__subtitle--modal">{modalSubtitle}</div>
                </div>
              )}
              <button
                type="button"
                className="how-demo-video__mute how-demo-video__mute--modal"
                onClick={toggleMuted}
                aria-label={isMuted ? 'Video is muted. Click to unmute.' : 'Video sound is on. Click to mute.'}
              >
                {isMuted ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default HowItWorks;
